import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import HistoryIcon from "@mui/icons-material/History";
import CustomDataGrid from "../../Components/CustomDataGrid";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useApiFetch } from "../../Utils/apiFetch";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { useLang } from "../../context/LanguageContext";
import { toast } from "react-toastify";

const AuditLog = () => {
    const apiFetch = useApiFetch();
    const authHeader = useAuthHeader();
    const { t } = useLang();

    const [logs, setLogs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [employeeFilter, setEmployeeFilter] = useState("");
    const [dateFilter, setDateFilter] = useState(null);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await apiFetch(`${REACT_APP_BACKEND_URL}/users`, {
                    headers: { Authorization: authHeader },
                });
                if (response.ok) {
                    const data = await response.json();
                    setEmployees(data);
                }
            } catch (err) {
                toast.error("Failed to load employee filter list");
            }
        };
        fetchEmployees();
        // apiFetch/authHeader omitted: apiFetch is useCallback-memoized
        // (stable identity) in useApiFetch, and this should only run once on
        // mount regardless of authHeader recomputing on every render (it's
        // not memoized by react-auth-kit) — eslint can't verify either point
        // statically.
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const params = new URLSearchParams();
                if (employeeFilter) params.set("employeeId", employeeFilter);
                if (dateFilter) params.set("date", dateFilter.startOf("day").toISOString());

                const response = await apiFetch(
                    `${REACT_APP_BACKEND_URL}/logs/get?${params.toString()}`,
                    { headers: { Authorization: authHeader } },
                );
                if (response.ok) {
                    const body = await response.json();
                    setLogs(
                        body.data.map((log) => ({
                            id: log.id,
                            createdAt: log.createdAt,
                            employeeName: log.employee?.name || "N/A",
                            details: log.details,
                        })),
                    );
                } else {
                    toast.error("Failed to load audit log");
                }
            } catch (err) {
                toast.error(err.message || "Failed to load audit log");
            }
        };
        fetchLogs();
        // apiFetch/authHeader omitted for the same reason as the effect above.
    }, [employeeFilter, dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const columns = [
        {
            field: "createdAt",
            headerName: t("DATE"),
            minWidth: 180,
            flex: 1,
            valueFormatter: (value) => new Date(value).toLocaleString(),
        },
        {
            field: "employeeName",
            headerName: t("EMPLOYEE"),
            minWidth: 150,
            flex: 1,
        },
        {
            field: "details",
            headerName: t("DETAILS"),
            minWidth: 300,
            flex: 2,
        },
    ];

    return (
        <Box sx={{ width: "100%" }}>
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #37474f 0%, #263238 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <HistoryIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Typography variant="h5" fontWeight={700}>
                        {t("AUDIT_LOG")}
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{t("EMPLOYEE")}</InputLabel>
                    <Select
                        value={employeeFilter}
                        label={t("EMPLOYEE")}
                        onChange={(e) => setEmployeeFilter(e.target.value)}
                    >
                        <MenuItem value="">{t("ALL_EMPLOYEES")}</MenuItem>
                        {employees.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id}>
                                {emp.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={t("DATE")}
                        value={dateFilter}
                        onChange={(newValue) => setDateFilter(newValue)}
                        slotProps={{ textField: { size: "small" } }}
                    />
                </LocalizationProvider>
            </Box>

            <CustomDataGrid columns={columns} rows={logs} perPage={20} hasEdit={false} />
        </Box>
    );
};

export default AuditLog;
